import { ComponentIds, HierarchyUtils, } from "@inter-ikea-core/core";
import { INSERT_TO_UPRIGHT } from "../constants/connectorKeys";
import { COMPONENT_IDS } from "@inter-ikea-storagetwo/range/dist/storage2ComponentIDs";
import { NO_MOUNTING_RAIL, NO_UPRIGHTS } from "../constants/errors";
import { getMountingRailIndexInSolution, indexMountingRailsInSolution, } from "../utils/mountingRailHelpers";
import { getUprights } from "../utils/sectionsHelpers";
import { getConnectorsComponent } from "@inter-ikea-storagetwo/range/dist/functions/componentGetters";
/**
 * Selects insert connectors on an upright.
 * @param _entity
 */
export const getInsertToUprightsConnectors = (_entity) => getConnectorsComponent(_entity).connectors.filter((connector) => connector.key.includes(INSERT_TO_UPRIGHT));
export const findHoleByConnectorKey = (connectorKey) => {
    const match = connectorKey.match(/\d+/);
    if (!(match === null || match === void 0 ? void 0 : match.length))
        throw new Error("No hole number found.");
    return parseInt(match[0], 10);
};
/**
 * Use regex to find a PointSeriesSubConnector
 * @param connection
 * @param startingHole
 * @param index
 */
const findConnectorByHoleNumber = (connection, startingHole, index) => {
    const regExRes = connection.hostConnector.key.match(/\d+/);
    const holeNumber = regExRes && regExRes[0];
    if (!holeNumber)
        throw new Error("NO HOLE!!");
    return startingHole + index === parseInt(holeNumber, 10);
};
/**
 * Selects insert connections
 *
 * @param Snap
 * @param holesDemandedByInsert
 * @param connections
 */
export const getInsertConnections = (Snap, holesDemandedByInsert, connections) => {
    const match = Snap.guestConnector.key.match(/\d+/);
    const number = match ? parseInt(match[0], 10) : 0;
    const startingHole = Snap.hostConnector.hole - number;
    return holesDemandedByInsert.reduce((acc, _curr, index) => {
        const res = connections.find((connection) => findConnectorByHoleNumber(connection, startingHole, index));
        return res ? [...acc, res] : acc;
    }, []);
};
/**
 * Selects the connection information for holes that are requested by an insert.
 *
 * @param connections
 * @param guestId
 * @param insertConnections
 */
export const selectInsertRequestedHoles = (connections, guestId, insertConnections) => {
    const occupiedHolesObject = generateOccupiedHolesObject(connections, guestId);
    return insertConnections.reduce((acc, curr) => [
        ...acc,
        occupiedHolesObject[curr.hostConnector.hole],
    ], []);
};
/**
 * Takes an array of array of numbers representing what occupies two holes. To be valid, the holes should be occupied
 * be none or two in total. If it is occupied by two, both the numbers should be the same.
 * @param arr
 */
export const validateInsertsRequestedConnectors = (arr) => {
    const flattened = arr.flat();
    if (!flattened.length)
        return true;
    if (flattened.length > 2)
        return false;
    return flattened[0] === flattened[1];
};
/**
 * Creates an object where key represent the hole number being occupied, and value is an array of
 * entity ids of the inserts occupying the holes.
 *
 * @param arr
 * @param excludeId
 */
export const generateOccupiedHolesObject = (arr, excludeId) => {
    return arr.reduce((acc, curr) => {
        if (excludeId && excludeId === curr.guestConnectorEntity.id)
            return acc;
        return Object.assign(Object.assign({}, acc), { [curr.hostConnector.hole]: [
                ...(acc[curr.hostConnector.hole] ? acc[curr.hostConnector.hole] : []),
                curr.guestConnectorEntity.id,
            ] });
    }, {});
};
/**
 * Returns all the insert connections to an upright.
 * @param entity
 */
export const getInsertToUprightConnections = (entity) => {
    var _a;
    const insertsConnections = (_a = entity
        .getComponent(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID)) === null || _a === void 0 ? void 0 : _a.connections.filter((connection) => connection.guestConnectorEntity.hasComponent(COMPONENT_IDS.INSERT));
    return insertsConnections || [];
};
/**
 * Validates if an insert connection is supposed to be valid on a given upright in relation
 * to existing insert on that upright.
 *
 * @param Snap
 * @param upright
 */
export const getInsertConnectionValidity = (Snap, upright) => {
    const connections = getInsertToUprightConnections(upright);
    const holesDemandedByInsert = getInsertToUprightsConnectors(Snap.guest);
    const insertConnectors = getInsertConnections(Snap, holesDemandedByInsert, connections);
    const holes = selectInsertRequestedHoles(connections, Snap.guest.id, insertConnectors);
    return validateInsertsRequestedConnectors(Object.values(holes));
};
export const calculateLeftUprightConnections = (snapActionCandidate, rightUpright) => {
    const mountingRail = HierarchyUtils.parent(rightUpright);
    if (!mountingRail)
        throw new Error(NO_MOUNTING_RAIL);
    const indexedMountingRails = indexMountingRailsInSolution(mountingRail);
    const indexOfMountingRail = getMountingRailIndexInSolution(mountingRail);
    if (indexOfMountingRail === 0)
        return true;
    const mountingRailOnTheLeft = indexedMountingRails[indexOfMountingRail - 1];
    const { leftUpright } = getUprights(mountingRailOnTheLeft);
    if (!leftUpright)
        throw new Error(NO_UPRIGHTS);
    return getInsertConnectionValidity(snapActionCandidate, leftUpright);
};
